import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import dermatoApi, {
  FormStatus,
  LesionFull,
  DermatoForm,
  DermatoFormProgress,
  type DermatoScreeningResult,
} from '../../../api/dermatoApi';
import { Patient, patientApi } from '../../../api/patientApi';

/**
 * Handler-ul real de salvare al unui pas (cel din butonul intern al pasului).
 * Returnează true dacă pasul e într-o stare salvată (nimic de salvat sau
 * salvare reușită); false dacă validarea sau salvarea a eșuat — erorile sunt
 * deja afișate de pas (validare inline) sau de interceptorul global axios.
 */
export type StepSaveHandler = () => Promise<boolean>;

interface DermatoWizardState {
  patient: Patient | null;
  form: DermatoForm | null;
  lesions: LesionFull[];
  selectedLesionId: string | null;
  loading: boolean;
  error: string | null;
  /** D1 — true dacă utilizatorul a interacționat cu pasul curent după ultima salvare. */
  stepDirty: boolean;
  /** D1 — progresul real pe cei 15 pași (server-side); null până la primul răspuns. */
  progress: DermatoFormProgress | null;
  /**
   * 2.9.3 — verdictul de screening al formularului (null dacă nu există).
   * Concluzia `SCREENING_REFUZAT` înseamnă „EXCLUS DIN PROGRAM" (refuz înregistrat
   * la înrolare, PAS 3) — episodul e închis și pașii nu mai sunt accesibili.
   */
  screeningResult: DermatoScreeningResult | null;
}

interface DermatoWizardActions {
  selectLesion: (id: string | null) => void;
  refreshLesions: () => Promise<void>;
  saveForm: (patch: Partial<DermatoForm>) => Promise<DermatoForm | null>;
  saveDraft: () => Promise<void>;
  submit: () => Promise<boolean>;
  addLesion: (data: Parameters<typeof dermatoApi.lesions.create>[0]) => Promise<LesionFull | null>;
  updateLesion: (id: string, data: Parameters<typeof dermatoApi.lesions.update>[1]) => Promise<LesionFull | null>;
  removeLesion: (id: string) => Promise<void>;
  registerStepSave: (fn: StepSaveHandler | null) => void;
  /** Rulează handler-ul pasului curent; true dacă nu există handler sau salvarea a reușit. */
  runStepSave: () => Promise<boolean>;
  /** D1 — marchează pasul curent ca având modificări nesalvate. */
  markStepDirty: () => void;
  /** D1 — curăță flag-ul de modificări (după salvare sau la schimbarea pasului). */
  resetStepDirty: () => void;
  /** D1 — reîncarcă progresul real pe pași de la server. */
  refreshProgress: () => Promise<void>;
  /** 2.9.3 — reîncarcă verdictul de screening (după accept/refuz). */
  refreshScreeningResult: () => Promise<void>;
}

export type DermatoWizardContextValue = DermatoWizardState & DermatoWizardActions;

const Ctx = createContext<DermatoWizardContextValue | null>(null);

export const useDermatoWizard = (): DermatoWizardContextValue => {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useDermatoWizard must be used inside DermatoWizardProvider');
  }
  return v;
};

/**
 * Hook pentru pași: înregistrează handler-ul real de salvare al pasului la
 * mount (și la fiecare render, ca să prindă closure-ul curent) și îl curăță
 * la unmount, ca „Salvează și continuă" / „Salvează draft" din layout să
 * folosească exact logica butonului intern al pasului.
 */
export const useRegisterStepSave = (handler: StepSaveHandler): void => {
  const { registerStepSave } = useDermatoWizard();
  useEffect(() => {
    registerStepSave(handler);
    return () => registerStepSave(null);
  });
};

interface ProviderProps {
  patientId: string;
  editFormId?: string | null;
  children: ReactNode;
}

export const DermatoWizardProvider = ({ patientId, editFormId, children }: ProviderProps) => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [form, setForm] = useState<DermatoForm | null>(null);
  const [lesions, setLesions] = useState<LesionFull[]>([]);
  const [selectedLesionId, setSelectedLesionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stepDirty, setStepDirty] = useState(false);
  const [progress, setProgress] = useState<DermatoFormProgress | null>(null);
  const [screeningResult, setScreeningResult] = useState<DermatoScreeningResult | null>(null);
  const stepSaveRef = useRef<StepSaveHandler | null>(null);

  const ensureForm = useCallback(async (existingPatient: Patient): Promise<DermatoForm> => {
    if (editFormId) {
      const f = await dermatoApi.getById(editFormId);
      return f;
    }
    const drafts = await dermatoApi.list({
      patientId: existingPatient.id,
      status: FormStatus.DRAFT,
      limit: 1,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
    });
    if (drafts.data.length > 0) {
      return drafts.data[0];
    }
    return dermatoApi.create({
      patientId: existingPatient.id,
      programType: existingPatient.programType,
    });
  }, [editFormId]);

  const refreshLesions = useCallback(async () => {
    if (!form) return;
    const r = await dermatoApi.lesions.list({ formId: form.id, limit: 100 });
    setLesions(r.data);
  }, [form]);

  const refreshProgress = useCallback(async () => {
    if (!form) return;
    try {
      const p = await dermatoApi.getProgress(form.id);
      setProgress(p);
    } catch (e) {
      // Progresul e auxiliar — nu blocăm wizard-ul dacă endpoint-ul pică.
      console.error('Eroare la încărcarea progresului', e);
    }
  }, [form]);

  const markStepDirty = useCallback(() => setStepDirty(true), []);
  const resetStepDirty = useCallback(() => setStepDirty(false), []);

  /** 2.9.3 — verdictul de screening (null dacă formularul nu are verdict). */
  const refreshScreeningResult = useCallback(async () => {
    if (!form) return;
    try {
      const r = await dermatoApi.screeningResult.getByForm(form.id);
      setScreeningResult(r);
    } catch (e) {
      console.error('Eroare la încărcarea verdictului de screening', e);
    }
  }, [form]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        setLoading(true);
        setError(null);
        const p = await patientApi.getById(patientId);
        if (cancelled) return;
        setPatient(p);

        const f = await ensureForm(p);
        if (cancelled) return;
        setForm(f);

        const r = await dermatoApi.lesions.list({ formId: f.id, limit: 100 });
        if (cancelled) return;
        setLesions(r.data);

        const prog = await dermatoApi.getProgress(f.id).catch(() => null);
        if (cancelled) return;
        setProgress(prog);

        const scr = await dermatoApi.screeningResult.getByForm(f.id).catch(() => null);
        if (cancelled) return;
        setScreeningResult(scr);
      } catch (e: any) {
        // Interceptorul global axios afișează deja toast-ul de eroare.
        const msg = e?.response?.data?.message ?? e?.message ?? 'Eroare la inițializarea wizard-ului';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [patientId, ensureForm]);

  const saveForm = useCallback(async (patch: Partial<DermatoForm>): Promise<DermatoForm | null> => {
    if (!form) return null;
    try {
      const updated = await dermatoApi.update(form.id, patch as any);
      setForm(updated);
      setStepDirty(false);
      return updated;
    } catch (e) {
      // Interceptorul global axios afișează deja toast-ul de eroare.
      console.error('Eroare la salvarea formularului', e);
      return null;
    }
  }, [form]);

  const registerStepSave = useCallback((fn: StepSaveHandler | null) => {
    stepSaveRef.current = fn;
  }, []);

  const runStepSave = useCallback(async (): Promise<boolean> => {
    const fn = stepSaveRef.current;
    if (!fn) return true;
    try {
      const saved = await fn();
      if (saved) setStepDirty(false);
      return saved;
    } catch (e) {
      console.error('Eroare la salvarea pasului curent', e);
      return false;
    }
  }, []);

  const saveDraft = useCallback(async () => {
    if (!stepSaveRef.current) {
      message.info('Nicio modificare de salvat pe acest pas');
      return;
    }
    const saved = await runStepSave();
    if (saved) {
      message.success('Draft salvat');
    }
  }, [runStepSave]);

  const submit = useCallback(async (): Promise<boolean> => {
    if (!form) return false;
    try {
      const updated = await dermatoApi.submit(form.id);
      setForm(updated);
      message.success('Formular trimis spre revizuire');
      return true;
    } catch (e) {
      // Interceptorul global axios afișează deja toast-ul de eroare.
      console.error('Eroare la trimiterea formularului', e);
      return false;
    }
  }, [form]);

  const addLesion = useCallback(async (data: Parameters<typeof dermatoApi.lesions.create>[0]) => {
    try {
      const created = await dermatoApi.lesions.create(data);
      setLesions((prev) => [...prev, created]);
      setSelectedLesionId(created.id);
      setStepDirty(false);
      setProgress((prev) => prev ? {
        ...prev,
        hasLesions: true,
        lesionCount: prev.lesionCount + 1,
        steps: prev.steps.map((s, i) => (i === 2 ? true : s)),
        completedCount: prev.steps[2] ? prev.completedCount : prev.completedCount + 1,
      } : prev);
      message.success(`Leziune ${created.clinicalCode} creată`);
      return created;
    } catch (e) {
      // Interceptorul global axios afișează deja toast-ul de eroare.
      console.error('Eroare la crearea leziunii', e);
      return null;
    }
  }, []);

  const updateLesion = useCallback(async (id: string, data: Parameters<typeof dermatoApi.lesions.update>[1]) => {
    try {
      const updated = await dermatoApi.lesions.update(id, data);
      setLesions((prev) => prev.map((l) => (l.id === id ? updated : l)));
      setStepDirty(false);
      return updated;
    } catch (e) {
      // Interceptorul global axios afișează deja toast-ul de eroare.
      console.error('Eroare la actualizarea leziunii', e);
      return null;
    }
  }, []);

  const removeLesion = useCallback(async (id: string) => {
    try {
      await dermatoApi.lesions.delete(id);
      setLesions((prev) => prev.filter((l) => l.id !== id));
      setSelectedLesionId((prev) => (prev === id ? null : prev));
      setStepDirty(false);
      message.success('Leziune ștearsă');
      // Ștergerea poate „goli" și pașii 4-15 (sub-entitățile leziunii) — reîncărcăm progresul.
      void refreshProgress();
    } catch (e) {
      // Interceptorul global axios afișează deja toast-ul de eroare.
      console.error('Eroare la ștergerea leziunii', e);
    }
  }, [refreshProgress]);

  const value = useMemo<DermatoWizardContextValue>(
    () => ({
      patient,
      form,
      lesions,
      selectedLesionId,
      loading,
      error,
      stepDirty,
      progress,
      screeningResult,
      selectLesion: setSelectedLesionId,
      refreshLesions,
      saveForm,
      saveDraft,
      submit,
      addLesion,
      updateLesion,
      removeLesion,
      registerStepSave,
      runStepSave,
      markStepDirty,
      resetStepDirty,
      refreshProgress,
      refreshScreeningResult,
    }),
    [patient, form, lesions, selectedLesionId, loading, error, stepDirty, progress, screeningResult, refreshLesions, saveForm, saveDraft, submit, addLesion, updateLesion, removeLesion, registerStepSave, runStepSave, markStepDirty, resetStepDirty, refreshProgress, refreshScreeningResult]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
